import { useNavigation } from 'expo-router'
import type { ReactElement } from 'react'
import { useEffect } from 'react'
import { useWindowDimensions } from 'react-native'
import { BREAKPOINTS } from '@/lib/config'
import { HeaderButton, HeaderButtonGroup, HeaderButtonLink } from '@/ui/HeaderAction'

type HeaderButtonType = 'edit' | 'save' | 'create' | 'delete' | 'create-save' | 'export'

interface HeaderActionConfig {
    type: HeaderButtonType
    onPress: () => void
    title?: string
    loading?: boolean
    loadingTitle?: string
    disabled?: boolean
}

interface HeaderLinkConfig {
    type: HeaderButtonType
    href: string
    title?: string
}

type HeaderButtonConfig = HeaderActionConfig | HeaderLinkConfig

interface UseHeaderOptionsParams {
    buttons: HeaderButtonConfig | HeaderButtonConfig[]
    title?: string
    smallScreenTitle?: string
    enabled?: boolean
}

function isLinkConfig(config: HeaderButtonConfig): config is HeaderLinkConfig {
    return 'href' in config
}

function renderButton(config: HeaderButtonConfig, index: number) {
    if (isLinkConfig(config)) {
        return (
            <HeaderButtonLink
                key={index}
                type={config.type}
                href={config.href}
                title={config.title}
            />
        )
    }

    return (
        <HeaderButton
            key={index}
            type={config.type}
            onPress={config.onPress}
            title={config.title}
            loading={config.loading}
            loadingTitle={config.loadingTitle}
            disabled={config.disabled}
        />
    )
}

export function useHeaderOptions({
    buttons,
    title,
    smallScreenTitle,
    enabled = true,
}: UseHeaderOptionsParams): void {
    const navigation = useNavigation()
    const { width } = useWindowDimensions()
    const isSmallScreen = width < BREAKPOINTS.md

    useEffect(() => {
        if (!enabled) return

        const buttonConfigs = Array.isArray(buttons) ? buttons : [buttons]
        const headerRight =
            buttonConfigs.length === 1
                ? () => renderButton(buttonConfigs[0], 0)
                : () => (
                      <HeaderButtonGroup>
                          {buttonConfigs.map((config, index) => renderButton(config, index))}
                      </HeaderButtonGroup>
                  )

        const resolvedTitle = isSmallScreen && smallScreenTitle ? smallScreenTitle : title
        const opts: { headerRight: () => ReactElement; title?: string } = { headerRight }
        if (resolvedTitle !== undefined) {
            opts.title = resolvedTitle
        }

        navigation.setOptions(opts)
    }, [navigation, buttons, title, smallScreenTitle, isSmallScreen, enabled])
}
